"use client";

import { useMemo, useState } from "react";
import { computeDuty } from "@/lib/stamp-duty/calculate";
import type { StampDutyRule } from "@/lib/stamp-duty/types";
import { StampDutyResultPanel } from "./StampDutyResultPanel";

interface StampDutyCalculatorProps {
  rules: StampDutyRule[];
  initialStateCode?: string;
  initialInstrumentSlug?: string;
  initialValue?: number;
  source?: string;
}

function uniqueStates(rules: StampDutyRule[]) {
  const states = new Map<string, { stateCode: string; stateName: string }>();
  for (const rule of rules) {
    if (!states.has(rule.stateCode)) {
      states.set(rule.stateCode, { stateCode: rule.stateCode, stateName: rule.stateName });
    }
  }
  return Array.from(states.values());
}

export function StampDutyCalculator({
  rules,
  initialStateCode,
  initialInstrumentSlug,
  initialValue = 5000000,
  source = "stamp-duty-calculator",
}: StampDutyCalculatorProps) {
  const defaultRule =
    rules.find((rule) => rule.stateCode === initialStateCode && rule.instrumentSlug === initialInstrumentSlug) ??
    rules.find((rule) => rule.stateCode === initialStateCode) ??
    rules[0];

  const [stateCode, setStateCode] = useState(defaultRule?.stateCode ?? "");
  const [instrumentSlug, setInstrumentSlug] = useState(defaultRule?.instrumentSlug ?? "");
  const [value, setValue] = useState(String(initialValue));
  const [selectedClauseAddOns, setSelectedClauseAddOns] = useState<string[]>([]);

  const states = useMemo(() => uniqueStates(rules), [rules]);

  const instruments = useMemo(
    () => rules.filter((rule) => rule.stateCode === stateCode),
    [rules, stateCode],
  );

  const effectiveInstrumentSlug = instruments.some((instrument) => instrument.instrumentSlug === instrumentSlug)
    ? instrumentSlug
    : instruments[0]?.instrumentSlug ?? "";

  const selectedRule = useMemo(
    () => rules.find((rule) => rule.stateCode === stateCode && rule.instrumentSlug === effectiveInstrumentSlug),
    [effectiveInstrumentSlug, rules, stateCode],
  );

  const clauseAddOns = selectedRule?.clauseAddOns ?? [];
  const effectiveClauseAddOns = selectedClauseAddOns.filter((addOnId) =>
    clauseAddOns.some((addOn) => addOn.id === addOnId),
  );

  function handleStateChange(nextStateCode: string) {
    setStateCode(nextStateCode);
    setInstrumentSlug(rules.find((rule) => rule.stateCode === nextStateCode)?.instrumentSlug ?? "");
    setSelectedClauseAddOns([]);
  }

  function handleInstrumentChange(nextInstrumentSlug: string) {
    setInstrumentSlug(nextInstrumentSlug);
    setSelectedClauseAddOns([]);
  }

  function toggleClauseAddOn(addOnId: string) {
    setSelectedClauseAddOns((current) =>
      current.includes(addOnId)
        ? current.filter((id) => id !== addOnId)
        : [...current, addOnId],
    );
  }

  const numericValue = Number(value);
  const canCompute = !!selectedRule && Number.isFinite(numericValue) && (numericValue > 0 || selectedRule.valueBasis === "fixed_none");
  const breakdown = selectedRule && canCompute
    ? computeDuty(selectedRule, {
        value: Math.max(0, numericValue),
        selectedClauseAddOns: effectiveClauseAddOns,
      })
    : null;

  if (!defaultRule) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-700">
        Stamp duty rules are not configured yet.
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
      <section aria-label="Stamp duty inputs" className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm md:p-6">
        <div>
          <p className="text-sm font-semibold uppercase text-primary">Free legal tool</p>
          <h2 className="mt-2 font-serif text-4xl text-gray-950">Stamp Duty Calculator</h2>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            Estimate stamp duty from a state, instrument, and chargeable value using the loaded
            Schedule rates.
          </p>
        </div>

        <div className="mt-6 space-y-5">
          <div>
            <label htmlFor="stamp-duty-state" className="block text-sm font-medium text-gray-800">
              State
            </label>
            <select
              id="stamp-duty-state"
              value={stateCode}
              onChange={(event) => handleStateChange(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            >
              {states.map((state) => (
                <option key={state.stateCode} value={state.stateCode}>
                  {state.stateName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="stamp-duty-instrument" className="block text-sm font-medium text-gray-800">
              Instrument
            </label>
            <select
              id="stamp-duty-instrument"
              value={effectiveInstrumentSlug}
              onChange={(event) => handleInstrumentChange(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            >
              {instruments.map((instrument) => (
                <option key={instrument.instrumentSlug} value={instrument.instrumentSlug}>
                  {instrument.instrumentName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="stamp-duty-value" className="block text-sm font-medium text-gray-800">
              {selectedRule?.valueLabel || "Value involved"}
            </label>
            <div className="mt-2 flex min-h-11 items-center rounded-md border border-gray-300 bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15">
              <span className="px-3 text-gray-500">₹</span>
              <input
                id="stamp-duty-value"
                type="number"
                min="0"
                step="1000"
                inputMode="numeric"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                className="min-h-10 w-full rounded-md border-0 px-2 text-sm text-gray-900 outline-none"
              />
            </div>
          </div>

          {clauseAddOns.length > 0 && (
            <fieldset className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <legend className="px-1 text-sm font-medium text-gray-800">Additional clauses</legend>
              <div className="mt-2 grid gap-2">
                {clauseAddOns.map((addOn) => (
                  <label
                    key={addOn.id}
                    htmlFor={`stamp-duty-clause-${addOn.id}`}
                    className="flex min-h-10 items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                  >
                    <input
                      id={`stamp-duty-clause-${addOn.id}`}
                      type="checkbox"
                      checked={effectiveClauseAddOns.includes(addOn.id)}
                      onChange={() => toggleClauseAddOn(addOn.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20"
                    />
                    <span>{addOn.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
        </div>
      </section>

      {selectedRule && breakdown ? (
        <StampDutyResultPanel
          rule={selectedRule}
          value={Math.max(0, numericValue)}
          breakdown={breakdown}
          source={source}
          selectedClauseAddOns={effectiveClauseAddOns}
        />
      ) : (
        <section className="rounded-lg border border-gray-200 bg-white p-6 text-sm leading-6 text-gray-600">
          Enter a positive value to see the stamp duty, working, statutory reference, and disclaimer.
        </section>
      )}
    </div>
  );
}
